/**
 * Escalation Modal Component
 * ==========================
 * Modal for escalating cases to supervisors or compliance officers.
 * Features:
 * - Team member selection dropdown
 * - Required reason textarea
 * - Optional priority override
 * - Current escalation level display
 */

import React, { useState } from 'react';
import {
  X,
  ChevronUp,
  AlertTriangle,
  User,
  Loader2,
} from 'lucide-react';
import { useTeamMembers } from '../../hooks/useSettings';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../auth';

const priorityOptions = [
  { value: '', label: 'Keep current priority' },
  { value: 'critical', label: 'Critical - Immediate attention' },
  { value: 'high', label: 'High - Same day' },
  { value: 'medium', label: 'Medium - Within 48 hours' },
  { value: 'low', label: 'Low - When available' },
];

export default function EscalationModal({
  isOpen,
  onClose,
  onEscalate,
  isLoading,
  currentEscalationLevel = 0,
  currentPriority,
}) {
  const toast = useToast();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    escalate_to: '',
    reason: '',
    priority_override: '',
  });

  const { data: teamData, isLoading: loadingTeam } = useTeamMembers();
  const teamMembers = teamData?.members || teamData || [];

  // Filter to supervisors/managers for escalation (exclude current user)
  const eligibleMembers = teamMembers.filter(
    (m) => m.user_id !== user?.sub && m.id !== user?.sub
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.escalate_to) {
      toast.error('Please select who to escalate to');
      return;
    }

    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for escalation');
      return;
    }

    onEscalate({
      escalate_to: formData.escalate_to,
      reason: formData.reason.trim(),
      priority_override: formData.priority_override || null,
    });
  };

  const handleClose = () => {
    setFormData({ escalate_to: '', reason: '', priority_override: '' });
    onClose();
  };

  if (!isOpen) return null;

  const nextLevel = currentEscalationLevel + 1;
  const levelLabels = {
    1: 'Level 1 (Supervisor)',
    2: 'Level 2 (Compliance Manager)',
    3: 'Level 3 (BSA Officer)',
    4: 'Level 4+ (Executive)',
  };
  const nextLevelLabel = levelLabels[nextLevel] || `Level ${nextLevel}`;

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <ChevronUp size={20} style={{ color: 'var(--color-warning)' }} />
            <h2 style={styles.title}>Escalate Case</h2>
          </div>
          <button style={styles.closeBtn} onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Level Indicator */}
        <div style={styles.levelIndicator}>
          <div style={styles.levelRow}>
            <span style={styles.levelLabel}>Current Level:</span>
            <span style={styles.levelValue}>
              {currentEscalationLevel === 0 ? 'None' : `Level ${currentEscalationLevel}`}
            </span>
          </div>
          <div style={styles.levelRow}>
            <span style={styles.levelLabel}>Escalating to:</span>
            <span style={{ ...styles.levelValue, color: 'var(--color-warning)' }}>
              {nextLevelLabel}
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {/* Escalate To */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Escalate To <span style={styles.required}>*</span>
              </label>
              {loadingTeam ? (
                <div style={styles.loading}>
                  <Loader2 size={14} style={styles.spinner} />
                  <span>Loading team members...</span>
                </div>
              ) : eligibleMembers.length === 0 ? (
                <div style={styles.warning}>
                  <AlertTriangle size={14} />
                  <span>No team members available for escalation</span>
                </div>
              ) : (
                <div style={styles.radioGroup}>
                  {eligibleMembers.map((member) => {
                    const memberId = member.user_id || member.id;
                    const memberName = member.name || member.email;
                    const memberRole = member.role || 'Team Member';

                    return (
                      <label key={memberId} style={styles.radioOption}>
                        <input
                          type="radio"
                          name="escalate_to"
                          value={memberId}
                          checked={formData.escalate_to === memberId}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, escalate_to: e.target.value }))
                          }
                          style={styles.radioInput}
                        />
                        <div style={styles.radioContent}>
                          <div style={styles.radioAvatar}>
                            {memberName?.[0]?.toUpperCase() || <User size={12} />}
                          </div>
                          <div style={styles.radioInfo}>
                            <span style={styles.radioName}>{memberName}</span>
                            <span style={styles.radioRole}>{memberRole}</span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reason */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Reason for Escalation <span style={styles.required}>*</span>
              </label>
              <textarea
                style={styles.textarea}
                value={formData.reason}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="Describe why this case needs escalation..."
                rows={4}
                required
              />
            </div>

            {/* Priority Override */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Priority Override</label>
              <select
                style={styles.select}
                value={formData.priority_override}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, priority_override: e.target.value }))
                }
              >
                {priorityOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.value === '' && currentPriority ? ` (${currentPriority})` : ''}
                  </option>
                ))}
              </select>
              <p style={styles.hint}>
                Optionally increase the priority when escalating.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={styles.submitBtn}
              disabled={isLoading || !formData.escalate_to || !formData.reason.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} style={styles.spinner} />
                  Escalating...
                </>
              ) : (
                <>
                  <ChevronUp size={16} />
                  Escalate Case
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-primary)',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid var(--border-color)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    borderRadius: '4px',
    transition: 'all 0.15s',
  },
  levelIndicator: {
    padding: '16px 20px',
    background: 'var(--bg-secondary)',
    borderBottom: '1px solid var(--border-color)',
  },
  levelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  levelLabel: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
  },
  levelValue: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  body: {
    padding: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  required: {
    color: 'var(--color-danger)',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--bg-secondary)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    border: '1px solid transparent',
  },
  radioInput: {
    accentColor: 'var(--color-primary)',
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  radioContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
  },
  radioAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'var(--bg-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  radioInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  radioName: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  radioRole: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '100px',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    cursor: 'pointer',
  },
  hint: {
    margin: '6px 0 0',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px',
    borderTop: '1px solid var(--border-color)',
  },
  cancelBtn: {
    padding: '10px 16px',
    background: 'transparent',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    background: 'var(--color-warning)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    color: 'var(--text-muted)',
    fontSize: '13px',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: 'var(--color-warning-bg)',
    color: 'var(--color-warning)',
    borderRadius: '8px',
    fontSize: '13px',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
};
